import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  User as FirebaseUser,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import { 
  doc, 
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { User, UserRole } from '../types/firebase';
import { getApiBaseUrl } from '@/lib/environment';

const googleProvider = new GoogleAuthProvider();

/**
 * Interface for invitation data returned from Firestore
 */
interface InvitationData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  roleId?: string | null;
  companyId: string;
  token: string;
  status: 'pending' | 'completed' | 'expired';
  createdAt: Timestamp;
  expiresAt: Timestamp;
  completedAt?: Timestamp;
  userId?: string;
}

/**
 * Check if there is a pending invitation for the given email
 * @param email - The email to check for invitations
 */
export const checkForPendingInvitation = async (email: string): Promise<InvitationData | null> => {
  try {
    const invitationsCollection = collection(db, 'invitations');
    const q = query(
      invitationsCollection, 
      where('email', '==', email),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as InvitationData;
  } catch (error) {
    console.error('Error checking for pending invitation:', error);
    return null;
  }
};

/**
 * Update a user entry with data from their invitation
 * @param uid - Firebase Auth UID
 * @param invitationData - The invitation data to apply
 */
export const updateUserWithInvitationData = async (uid: string, invitationData: InvitationData): Promise<boolean> => {
  try {
    // Update the user record with the invitation data
    await updateDoc(doc(db, 'users', uid), {
      role: invitationData.role,
      roleId: invitationData.roleId || null,
      companyId: invitationData.companyId,
      status: 'active',
      isActive: true,
      invitationId: invitationData.id,
      updatedAt: new Date()
    });
    
    // Mark invitation as completed
    await updateDoc(doc(db, 'invitations', invitationData.id), {
      status: 'completed',
      completedAt: new Date(),
      userId: uid
    });
    
    return true;
  } catch (error) {
    console.error('Error updating user with invitation data:', error);
    return false;
  }
};

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    console.log('[Google Auth] Processing login for:', user.email);

    // Check if user exists by UID
    const userDoc = await getDoc(doc(db, 'users', user.uid));

    if (userDoc.exists()) {
      // User exists - update and proceed
      console.log('[Google Auth] ✅ User found:', user.email);
      await updateDoc(doc(db, 'users', user.uid), {
        lastLoginAt: new Date(),
        updatedAt: new Date()
      });

      // Check for invitation to update user data if needed
      const invitation = await checkForPendingInvitation(user.email || '');
      if (invitation) {
        console.log('[Google Auth] Processing pending invitation for existing user:', user.email);
        await updateUserWithInvitationData(user.uid, invitation);
      }

      return user;
    }

    // User doesn't exist - check for invitation
    console.log('[Google Auth] User not found, checking invitation...');
    const invitation = await checkForPendingInvitation(user.email || '');

    if (invitation) {
      // Create user from invitation
      console.log('[Google Auth] ✅ Creating user from invitation:', user.email);
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || `${invitation.firstName} ${invitation.lastName}`,
        photoURL: user.photoURL || '',
        role: invitation.role,
        roleId: invitation.roleId,
        companyId: invitation.companyId,
        status: 'active',
        isActive: true,
        invitationId: invitation.id,
        metadata: {},
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: new Date()
      });

      // Mark invitation as completed
      await updateUserWithInvitationData(user.uid, invitation);
      return user;
    }

    // No user, no invitation - reject
    console.log('[Google Auth] ❌ User not found and no invitation:', user.email);
    await auth.signOut();
    throw new Error('USER_NOT_FOUND: Your account does not exist. Please contact your administrator for access.');

  } catch (error) {
    console.error('[Google Auth] Error:', error);
    throw error;
  }
};

export async function signInWithEmailAndPassword(email: string, password: string) {
  try {
    const result = await firebaseSignInWithEmailAndPassword(auth, email, password);
    const user = result.user;

    console.log('[Email Auth] Processing login for:', email);

    // Check if user exists by UID
    const userDoc = await getDoc(doc(db, 'users', user.uid));

    if (userDoc.exists()) {
      // User exists - update and proceed
      console.log('[Email Auth] ✅ User found:', email);
      await updateDoc(doc(db, 'users', user.uid), {
        lastLoginAt: new Date(),
        updatedAt: new Date()
      });

      // Check for invitation to update user data if needed
      const invitation = await checkForPendingInvitation(email);
      if (invitation) {
        console.log('[Email Auth] Processing pending invitation for existing user:', email);
        await updateUserWithInvitationData(user.uid, invitation);
      }

      return user;
    }

    // User doesn't exist - check for invitation
    console.log('[Email Auth] User not found, checking invitation...');
    const invitation = await checkForPendingInvitation(email);

    if (invitation) {
      // Create user from invitation
      console.log('[Email Auth] ✅ Creating user from invitation:', email);
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || `${invitation.firstName} ${invitation.lastName}`,
        // Don't include photoURL for email/password users
        role: invitation.role,
        roleId: invitation.roleId,
        companyId: invitation.companyId,
        status: 'active',
        isActive: true,
        invitationId: invitation.id,
        metadata: {},
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: new Date()
      });

      // Mark invitation as completed
      await updateUserWithInvitationData(user.uid, invitation);
      return user;
    }

    // No user, no invitation - reject
    console.log('[Email Auth] ❌ User not found and no invitation:', email);
    await auth.signOut();
    throw new Error('USER_NOT_FOUND: Your account does not exist. Please contact your administrator for access.');

  } catch (error) {
    console.error('[Email Auth] Error:', error);
    throw error;
  }
}

export const createUser = async (
  email: string, 
  password: string, 
  role: UserRole = 'user', 
  companyId?: string, 
  status: 'active' | 'pending' = 'active',
  invitationId?: string
) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: '',
      photoURL: '',
      role,
      roleId: null,
      companyId: companyId || null,
      status,
      isActive: status === 'active',
      invitationId: invitationId || null,
      metadata: {},
      createdAt: new Date(),
      updatedAt: new Date(),
      lastLoginAt: new Date()
    });
    
    return user;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const getCurrentUser = async (firebaseUser?: FirebaseUser): Promise<User | null> => {
  const currentUser = firebaseUser || auth.currentUser;
  if (!currentUser) {
    return null;
  }
  
  const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
  if (!userDoc.exists()) {
    return null;
  }
  
  const userData = userDoc.data();
  return {
    uid: currentUser.uid,
    email: currentUser.email || '',
    role: userData.role || 'user',
    companyId: userData.companyId,
    displayName: userData.displayName || currentUser.displayName || '',
    // Only include photoURL if it exists in userData or from Google
    ...(userData.photoURL || currentUser.photoURL ? { photoURL: userData.photoURL || currentUser.photoURL || '' } : {}),
    isActive: userData.isActive ?? true,
    status: userData.status || 'active',
    metadata: userData.metadata || {},
    createdAt: userData.createdAt,
    updatedAt: userData.updatedAt,
    lastLoginAt: userData.lastLoginAt
  } as User;
};

export const signOut = () => firebaseSignOut(auth);

/**
 * Role Management System
 * 
 * The application uses two parallel role systems that work together:
 * 
 * 1. Basic Role (user.role): A string value ('admin', 'manager', 'user', 'super_admin')
 *    that defines the user's general permission level.
 * 
 * 2. Custom Role (user.roleId): A reference to a role document in the 'roles' collection
 *    that defines custom granular permissions for the user.
 * 
 * When changing a user's role:
 * - For built-in admin/super_admin roles: We set roleId to null because these roles
 *   use built-in permission sets that are hardcoded.
 * - For other roles: We try to find a matching role document in the roles collection
 *   that corresponds to the role name, preferring default roles when available.
 * 
 * The permissions system checks both systems. If a custom roleId is provided,
 * those permissions are used. Otherwise, it falls back to the basic role permissions.
 */

/**
 * Updates a user's role and roleId in Firestore
 * @param uid - User ID to update
 * @param role - New role to assign
 * @param companyId - Company ID for the user (needed to fetch the correct role)
 * @param customRoleId - Optional specific roleId to assign (if known)
 */
export const updateUserRole = async (uid: string, role: UserRole, companyId: string, customRoleId?: string) => {
  try {
    // First, get the current user data to see if they're an admin
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    const isCurrentlyAdmin = userData.role === 'admin';
    const currentRoleId = userData.roleId;
    
    // Check if current role has admin permissions via roleId
    let hasCurrentAdminRole = false;
    if (currentRoleId) {
      const roleDoc = await getDoc(doc(db, 'roles', currentRoleId));
      if (roleDoc.exists()) {
        const roleData = roleDoc.data();
        hasCurrentAdminRole = roleData.name?.toLowerCase() === 'admin';
      }
    }
    
    // Determine if the user is currently an admin (either by role or roleId)
    const isUserAdmin = isCurrentlyAdmin || hasCurrentAdminRole;
    
    // If changing from admin to non-admin, check if this is the last admin
    const willBeAdmin = role === 'admin';
    let willHaveAdminRole = false;
    
    // Check if the new roleId (if provided) is an admin role
    if (customRoleId) {
      try {
        const roleDoc = await getDoc(doc(db, 'roles', customRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          willHaveAdminRole = roleData.name?.toLowerCase() === 'admin';
        }
      } catch (err) {
        console.warn('Error checking if new roleId is Admin:', err);
      }
    }
    
    // If the user is currently admin but will no longer be admin after this change
    if (isUserAdmin && !willBeAdmin && !willHaveAdminRole) {
      // Count admin users in the company
      const adminCount = await countAdminUsers(companyId);
      console.log(`Current admin count: ${adminCount}, attempting to downgrade an admin user`);
      
      // If this is the only admin user, prevent the change
      if (adminCount <= 1) {
        throw new Error('Cannot remove admin role from the last admin user. Companies must maintain at least one admin user.');
      }
    }
    
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    // If downgrading from admin to any other role, make sure role field is set to "user"
    if (isUserAdmin && !willBeAdmin && !willHaveAdminRole) {
      updateData.role = 'user';
    } else {
      updateData.role = role;
    }

    // If a specific roleId is provided, use it
    if (customRoleId) {
      updateData.roleId = customRoleId;
      
      // Check if the assigned role is an Admin role
      try {
        const roleDoc = await getDoc(doc(db, 'roles', customRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          // If this is an Admin role, make sure the role field is set to 'admin'
          if (roleData.name && roleData.name.toLowerCase() === 'admin') {
            updateData.role = 'admin';
          }
        }
      } catch (err) {
        console.warn('Error checking if roleId is Admin when updating user role:', err);
        // Continue with the update even if this check fails
      }
    } 
    // For admin and super_admin roles, set roleId to null as they use built-in permissions
    else if (role === 'admin' || role === 'super_admin') {
      updateData.roleId = null;
      
      // If user is being set to admin, try to find and assign the Admin roleId
      if (role === 'admin') {
        try {
          const rolesCollection = collection(db, 'roles');
          const q = query(
            rolesCollection,
            where('companyId', '==', companyId),
            where('name', '==', 'Admin')
          );
          
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            // Use the first matching Admin role
            updateData.roleId = snapshot.docs[0].id;
          }
        } catch (err) {
          console.warn('Error finding Admin role when setting user to admin:', err);
          // Continue with the update even if this fails
        }
      }
    } 
    // For other roles (manager, user), find the corresponding default role in the company
    else {
      // Query the roles collection to find a default role matching the role name
      const rolesCollection = collection(db, 'roles');
      const q = query(
        rolesCollection,
        where('companyId', '==', companyId),
        where('name', '==', role),
        where('isDefault', '==', true)
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Use the first matching default role
        updateData.roleId = snapshot.docs[0].id;
      } else {
        // If no default role found, try to find any role with the matching name
        const nonDefaultQuery = query(
          rolesCollection,
          where('companyId', '==', companyId),
          where('name', '==', role)
        );
        
        const nonDefaultSnapshot = await getDocs(nonDefaultQuery);
        
        if (!nonDefaultSnapshot.empty) {
          updateData.roleId = nonDefaultSnapshot.docs[0].id;
        } else {
          // If still no matching role, set roleId to null
          updateData.roleId = null;
          console.warn(`No matching role found for '${role}' in company '${companyId}'. Setting roleId to null.`);
        }
      }
    }

    // Double-check that we're not about to remove the last admin
    if (isUserAdmin && !willBeAdmin && !willHaveAdminRole) {
      // Perform one final check to ensure we still have at least one other admin
      const adminCount = await countAdminUsers(companyId);
      if (adminCount <= 1) {
        throw new Error('Cannot remove admin role from the last admin user. Companies must maintain at least one admin user.');
      }
    }

    // Update the user document
    await setDoc(doc(db, 'users', uid), updateData, { merge: true });
    
    console.log(`Updated user ${uid} role to ${updateData.role} with roleId: ${updateData.roleId || 'null'}`);
    
    return {
      role: updateData.role,
      roleId: updateData.roleId
    };
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Helper function to count admin users in a company
 * @param companyId - Company ID to check
 * @returns Number of admin users in the company
 */
async function countAdminUsers(companyId: string): Promise<number> {
  try {
    // Query for users with admin role
    const adminRoleQuery = query(
      collection(db, 'users'),
      where('companyId', '==', companyId),
      where('role', '==', 'admin')
    );
    
    const adminRoleSnapshot = await getDocs(adminRoleQuery);
    const adminRoleCount = adminRoleSnapshot.size;
    
    // Query for users with admin permissions via roleId
    const usersSnapshot = await getDocs(
      query(
        collection(db, 'users'),
        where('companyId', '==', companyId)
      )
    );
    
    let adminRoleIdCount = 0;
    
    // Check each user with a roleId to see if it has admin permissions
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      // Skip users already counted in adminRoleCount
      if (userData.role === 'admin') continue;
      
      if (userData.roleId) {
        const roleDoc = await getDoc(doc(db, 'roles', userData.roleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          if (roleData.name?.toLowerCase() === 'admin') {
            adminRoleIdCount++;
          }
        }
      }
    }
    
    return adminRoleCount + adminRoleIdCount;
  } catch (error) {
    console.error('Error counting admin users:', error);
    return 0; // Default to 0 on error, which would prevent role changes
  }
}

/**
 * Generate a random verification code
 * @returns A 6-digit numerical code as a string
 */
export function generateVerificationCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Check if an email exists in the system and what sign-in methods are associated with it
 * @param email The email to check
 * @returns Object with exists flag and sign-in methods
 */
export async function checkEmailExists(email: string): Promise<{exists: boolean, methods: string[]}> {
  try {
    const methods = await fetchSignInMethodsForEmail(auth, email);
    return {
      exists: methods.length > 0,
      methods
    };
  } catch (error) {
    console.error('Error checking if email exists:', error);
    return {
      exists: false,
      methods: []
    };
  }
}

/**
 * Sends a password reset verification code to the user's email
 * @param email The user's email to send the verification code to
 * @returns Object with success flag and message
 */
export async function sendPasswordResetCode(email: string): Promise<{
  success: boolean;
  message: string;
  error?: string;
}> {
  try {
    // Call the server-side endpoint to send the verification code
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/auth/send-reset-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: result.message || 'An error occurred while sending the verification code.',
        error: result.error
      };
    }
    
    // Handle Google user case
    if (result.error === 'GOOGLE_USER') {
      return {
        success: false,
        message: result.message,
        error: 'GOOGLE_USER'
      };
    }
    
    return {
      success: true,
      message: result.message || 'Verification code sent to your email. Please check your inbox.'
    };
  } catch (error) {
    console.error('Failed to send password verification code:', error);
    return {
      success: false,
      message: "An error occurred while sending the verification code."
    };
  }
}

/**
 * Verify a password reset code
 * @param email The email associated with the verification code
 * @param code The verification code to verify
 * @returns Object with success flag and user ID if successful
 */
export async function verifyPasswordResetCode(email: string, code: string): Promise<{
  success: boolean;
  userId?: string;
  message: string;
}> {
  try {
    // Get the reset document
    const resetDoc = await getDoc(doc(db, 'passwordResets', email));
    
    if (!resetDoc.exists()) {
      return {
        success: false,
        message: "Invalid or expired verification code."
      };
    }
    
    const resetData = resetDoc.data();
    const expiresAt = resetData.expiresAt.toDate();
    const used = resetData.used;
    const attempts = resetData.attempts || 0;
    
    // Check if the reset has been used
    if (used) {
      return {
        success: false,
        message: "This verification code has already been used."
      };
    }
    
    // Check if the code has expired
    if (expiresAt < new Date()) {
      return {
        success: false,
        message: "The verification code has expired. Please request a new one."
      };
    }
    
    // Increment attempts counter
    await updateDoc(doc(db, 'passwordResets', email), {
      attempts: attempts + 1
    });
    
    // Verify the code
    if (resetData.code !== code) {
      // If too many attempts (5+), invalidate the code
      if (attempts >= 4) { // This will be the 5th attempt
        await updateDoc(doc(db, 'passwordResets', email), {
          used: true
        });
        
        return {
          success: false,
          message: "Too many incorrect attempts. Please request a new verification code."
        };
      }
      
      return {
        success: false,
        message: `Incorrect code. You have ${5 - (attempts + 1)} attempts remaining.`
      };
    }
    
    // Code is valid
    return {
      success: true,
      userId: resetData.userId,
      message: "Code verified successfully."
    };
  } catch (error) {
    console.error('Failed to verify password reset code:', error);
    return {
      success: false,
      message: "An error occurred while verifying the code."
    };
  }
}

/**
 * Reset user password after verification
 * @param email The user's email
 * @param newPassword The new password
 * @param verificationCode The verification code for confirmation
 * @returns Object with success flag and message
 */
export async function resetPassword(email: string, newPassword: string, verificationCode: string): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    // Call the server-side endpoint to reset the password
    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        newPassword,
        verificationCode
      })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: result.message || 'An error occurred while resetting your password.'
      };
    }
    
    return {
      success: true,
      message: result.message || 'Password has been reset successfully. You can now log in with your new password.'
    };
  } catch (error) {
    console.error('Failed to reset password:', error);
    return {
      success: false,
      message: "An error occurred while resetting your password."
    };
  }
} 
